import React, { useState } from 'react';
import { KeyRound, Eye, EyeOff, ShieldCheck, Check, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const rules = [
  { label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { label: 'One uppercase letter (A-Z)', test: (v) => /[A-Z]/.test(v) },
  { label: 'One lowercase letter (a-z)', test: (v) => /[a-z]/.test(v) },
  { label: 'One number (0-9)', test: (v) => /\d/.test(v) },
  { label: 'One special character (!@#$...)', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

const ChangePassword = () => {
  const { toast } = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ current: false, next: false, confirm: false });

  const strength = rules.filter((r) => r.test(next)).length;
  const strengthLabel = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'][strength];
  const strengthColor = ['bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-lime-500', 'bg-emerald-500', 'bg-emerald-600'][strength];

  const submit = (e) => {
    e.preventDefault();
    if (!current) return toast({ title: 'Enter current password' });
    if (strength < 4) return toast({ title: 'Password too weak', description: 'Meet at least 4 of the 5 requirements.' });
    if (next !== confirm) return toast({ title: 'Passwords do not match' });
    toast({ title: 'Password updated', description: 'Your account password has been changed successfully.' });
    setCurrent(''); setNext(''); setConfirm('');
  };

  return (
    <section className="px-4 md:px-10 py-6 pb-16">
      <div className="mx-auto max-w-[1240px]">
        <div className="rounded-3xl bg-[#fbf4dc] border border-emerald-900/15 shadow-2xl overflow-hidden">
          <div className="p-6 md:p-8 lg:p-10">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="font-serif text-[30px] md:text-[34px] text-emerald-950 tracking-tight">Portal Center</h2>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-800/40 bg-emerald-200/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" /> Real Estate
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <div className="rounded-xl bg-emerald-950 text-emerald-50 px-4 py-2 text-sm font-medium">Change Password</div>
            </div>

            <form onSubmit={submit} className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
              <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6 space-y-5">
                <div className="flex items-center gap-2">
                  <KeyRound size={16} className="text-emerald-700" />
                  <span className="text-emerald-900 font-semibold text-[15px]">Update Account Password</span>
                </div>

                <Field label="Current Password" required
                  show={show.current} toggle={() => setShow({ ...show, current: !show.current })}>
                  <input type={show.current ? 'text' : 'password'} value={current} onChange={(e) => setCurrent(e.target.value)} required placeholder="Enter your current password" className="portal-input pr-11" />
                </Field>

                <Field label="New Password" required
                  show={show.next} toggle={() => setShow({ ...show, next: !show.next })}>
                  <input type={show.next ? 'text' : 'password'} value={next} onChange={(e) => setNext(e.target.value)} required placeholder="Create a strong password" className="portal-input pr-11" />
                </Field>

                {/* Strength meter */}
                {next && (
                  <div>
                    <div className="h-1.5 rounded-full bg-emerald-900/10 overflow-hidden">
                      <div className={`h-full ${strengthColor} transition-all`} style={{ width: `${(strength / 5) * 100}%` }} />
                    </div>
                    <div className="mt-1.5 text-[12px] text-emerald-900/70">Strength: <span className="font-semibold text-emerald-900">{strengthLabel}</span></div>
                  </div>
                )}

                <Field label="Confirm New Password" required
                  show={show.confirm} toggle={() => setShow({ ...show, confirm: !show.confirm })}>
                  <input type={show.confirm ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Re-enter new password" className="portal-input pr-11" />
                </Field>

                <button type="submit" className="w-full rounded-xl bg-emerald-950 hover:bg-emerald-900 text-emerald-50 font-medium py-3.5 transition">
                  Update Password
                </button>
              </div>

              {/* Requirements panel */}
              <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 h-fit">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={16} className="text-emerald-700" />
                  <span className="text-emerald-900 font-semibold text-[15px]">Password Requirements</span>
                </div>
                <ul className="mt-4 space-y-2">
                  {rules.map((r) => {
                    const ok = r.test(next);
                    return (
                      <li key={r.label} className="flex items-center gap-2 text-[13px]">
                        <span className={`h-5 w-5 rounded-full flex items-center justify-center ${ok ? 'bg-emerald-500 text-emerald-950' : 'bg-emerald-900/10 text-emerald-900/40'}`}>
                          {ok ? <Check size={12} /> : <X size={12} />}
                        </span>
                        <span className={ok ? 'text-emerald-900' : 'text-emerald-900/60'}>{r.label}</span>
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-5 rounded-xl bg-amber-100/60 border border-amber-500/30 px-3.5 py-3 text-[12px] text-amber-900">
                  For extra security, enable 2FA under <span className="font-semibold">Profile → Authentication</span> after updating your password.
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, show, toggle, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/80 mb-2">
      {label}{required && <span className="text-emerald-700 ml-0.5">*</span>}
    </span>
    <div className="relative">
      {children}
      <button type="button" onClick={toggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-800/70 hover:text-emerald-950">
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  </label>
);

export default ChangePassword;
